import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import {
    Box,
    Grid,
    Paper,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import {
    useGetStatsQuery,
    useGetPerformanceGraphQuery,
    useGetPerformanceSplitQuery,
    useGetSinceActivationQuery
} from "src/context/api/businessOverview/api";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    setAutoTimeStep,
    setDatesForcedByActivation,
    setFilterCompare,
    setFilterDate,
    setFilterDateCompare,
    setFilterTimestep,
    setSavingsFormulaFilter,
} from "src/context/reducers/filter-settings/filterSlice";
import { hasCustomSavingsConfigured } from "src/layouts/dashboard/savings-formula-utils";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { differenceInCalendarDays, parseISO, subDays } from "date-fns";
import { fDate } from "src/utils/format-time";
import ChartPerformanceBusinessOverview from "src/sections/charts/chart-performance-business-overview";
import DataGridPerformanceTable from "src/components/data-grid/data-grid-performance-split-business-overview";
import FunnelKpiCard from "./funnel/FunnelKpiCard";

export default function BusinessOverview({
    disableTab,
    disableSinceActivation,
}: any) {
    const workspace: any = useCurrentWorkspace();
    const theme = useTheme();
    const dispatch = useDispatch();
    const filterStore = useSelector((state: any) => state.filters);
    const [value, setValue] = useState(0);
    const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set([0]));
    const [sinceActivate, setSinceActivate]: any = useState(null);
    const { t, i18n } = useTranslation();

    const setLast30Days = useCallback(() => {
        const todayMinus1 = subDays(new Date(), 1);
        const start = subDays(todayMinus1, 29);
        dispatch(
            setFilterDate({
                start: fDate(start, "yyyy-MM-dd"),
                end: fDate(todayMinus1, "yyyy-MM-dd"),
            })
        );
    }, [dispatch]);

    const { data: sinceActivationData } = useGetSinceActivationQuery(
        { workspaceId: workspace?.id },
        { skip: !workspace?.id }
    );

    const {
        data: statsData,
        error: statsError,
        isFetching: isFetchingStats,
    } = useGetStatsQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            compare_end_date: filterStore?.compareEnd,
            compare: filterStore?.compare,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        { skip: sinceActivate === null }
    );

    const { data: performanceGraphData, isFetching: isFetchingGraph, error: performanceGraphError } = useGetPerformanceGraphQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            savingsFormula: filterStore?.savingsFormulaFilter,
            compare_start_date: filterStore?.compareStart,
            timestep: filterStore?.timestep,
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        { skip: sinceActivate === null }
    );

    const hasInitializedActivation = useRef(false);

    useEffect(() => {
        if (hasInitializedActivation.current) return;

        // When a Search Performance section override is active, do NOT impose the
        // since-activation range. Still flip sinceActivate so the data queries
        // (skip: sinceActivate === null) run with the persisted range.
        if (filterStore?.searchPerfDateOverridden) {
            hasInitializedActivation.current = true;
            setSinceActivate(false);
            return;
        }

        if (disableSinceActivation) {
            hasInitializedActivation.current = true;
            setLast30Days();
            setSinceActivate(false);
            dispatch(setFilterCompare(false));
            dispatch(setDatesForcedByActivation(false));
            return;
        }

        if (!sinceActivationData) return;
        hasInitializedActivation.current = true;

        if (sinceActivationData?.first_entry_date) {
            const start = parseISO(sinceActivationData.first_entry_date);
            const end = subDays(new Date(), 1);

            dispatch(
                setFilterDate({
                    start: fDate(start, "yyyy-MM-dd"),
                    end: fDate(end, "yyyy-MM-dd"),
                    skipAlignment: true
                })
            );

            const diffDays = differenceInCalendarDays(end, start) + 1;
            const compareEnd = subDays(start, 1);
            const compareStart = subDays(compareEnd, diffDays - 1);

            dispatch(
                setFilterDateCompare({
                    compareStart: fDate(compareStart, "yyyy-MM-dd"),
                    compareEnd: fDate(compareEnd, "yyyy-MM-dd"),
                    skipAlignment: true
                })
            );
            dispatch(setFilterCompare(true));
            dispatch(setDatesForcedByActivation(true));
            setSinceActivate(true);
        } else {
            setLast30Days();
            setSinceActivate(false);
            dispatch(setFilterCompare(false));
            dispatch(setDatesForcedByActivation(false));
        }
    }, [sinceActivationData, disableSinceActivation, dispatch, setLast30Days, filterStore?.searchPerfDateOverridden]);

    const isCustom = workspace?.custom;
    const hasMultipleCountries = filterStore?.loadedCountries?.length > 1;
    const hasMultiplePlatforms = (filterStore?.loadedPlatform?.length || 0) >= 2;

    const getTabsOrder = useCallback(
        ({ country, platforms }: { country: boolean; platforms: boolean }) => {
            if (country && !platforms) return ["Countries", "Devices", "Date"];
            if (country && platforms) return ["Countries", "Platforms", "Devices", "Date"];
            if (!country && platforms) return ["Platforms", "Devices", "Date"];
            if (!country && !platforms) return ["Date", "Devices"];
            return ["Date"];
        },
        []
    );

    const tabsToShow = useMemo(() => {
        const tabs = getTabsOrder({
            country: hasMultipleCountries,
            platforms: hasMultiplePlatforms,
        });
        return isCustom ? tabs.filter((tab) => tab !== "Devices") : tabs;
    }, [hasMultipleCountries, hasMultiplePlatforms, isCustom, getTabsOrder]);

    // useEffect(() => {
    //     if (!hasInitializedTab && hasMultipleCountries === false && tabsToShow.includes("Date")) {
    //         const index = tabsToShow.indexOf("Date");
    //         setValue(index);
    //         setLoadedTabs(new Set([index]));
    //         setHasInitializedTab(true);
    //     }
    // }, [hasMultipleCountries, tabsToShow, hasInitializedTab]);

    type Tab = "Countries" | "Platforms" | "Devices" | string;

    const MAP: Record<string, string> = {
        Countries: "country",
        Platforms: "platform",
        Devices: "device",
    } as const;

    const splitOptions = useMemo<string[]>(
        () => tabsToShow.map((tab: Tab) => MAP[tab] ?? "date"),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tabsToShow]
    );

    const handleChange = useCallback(
        (event: any, newValue: number) => {
            setValue(newValue);
            setLoadedTabs((prev) => new Set(prev).add(newValue));
        },
        []
    );

    const {
        data: performanceSplitRows,
        isFetching: isFetchingSplitRows,
        error: performanceSplitRowsError,
    } = useGetPerformanceSplitQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            bqTable: workspace?.bqTable,
            compare: filterStore?.compare,
            compare_end_date: filterStore?.compareEnd,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            split: splitOptions[value],
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        {
            skip: sinceActivate === null || !loadedTabs.has(value),
        }
    );

    const isCustomSavings = filterStore?.savingsFormulaFilter === 'custom';

    const filteredStats: any = useMemo(() => {
        if (!statsData) return [];
        return [
            {
                title: t("dashboard.global.savings"),
                value: isCustomSavings
                    ? statsData?.bankingWidgetSummary?.savings_custom
                    : statsData?.bankingWidgetSummary?.savings_previous_month,
                compare: isCustomSavings
                    ? statsData?.bankingWidgetSummary?.savings_custom_compare
                    : statsData?.bankingWidgetSummary?.savings_previous_month_compare,
                isPercent: false,
                isCurrency: true,
                invertedColor: false,
                color: "success",
                definition: t("definitions.savings"),
                hideInfinity: true,
            },
            {
                title: t("dashboard.global.ad_cost_with_signal"),
                value: statsData?.bankingWidgetSummary?.ad_cost_with_cb,
                compare: statsData?.bankingWidgetSummary?.ad_cost_with_cb_compare,
                isPercent: false,
                color: "warning",
                isCurrency: true,
                invertedColor: true,
                definition: t("definitions.ad_cost_with_signal"),
            },
            {
                title: t("dashboard.global.ad_cost_without_signal"),
                value: statsData?.bankingWidgetSummary?.ad_cost_without_cb,
                compare: statsData?.bankingWidgetSummary?.ad_cost_without_cb_compare,
                isPercent: false,
                color: "error",
                isCurrency: true,
                invertedColor: true,
                definition: t("definitions.ad_cost_without_signal"),
            },
            {
                title: t("dashboard.brandTraffic.totalBrandTraffic"),
                value: statsData?.bankingWidgetSummary?.brand_traffic,
                compare: statsData?.bankingWidgetSummary?.brand_traffic_compare,
                isPercent: false,
                color: "secondary",
                isCurrency: false,
                invertedColor: false,
                definition: t("definitions.brand_traffic"),
            },
        ];
    }, [statsData, i18n.language, isCustomSavings]);

    useEffect(() => {
        if (workspace?.savings_custom_available && hasCustomSavingsConfigured(workspace?.savings)) {
            dispatch(setSavingsFormulaFilter('custom'));
        }
    }, [workspace?.savings, workspace?.savings_custom_available, dispatch]);

    useEffect(() => {
        if (workspace?.name.includes('Demo')) {
            dispatch(setAutoTimeStep(false))
            dispatch(setFilterTimestep('year_week'))
        }
    }, [dispatch, workspace])

    return (
        <Box px={"24px"}>
            {/* Stats Cards — 12px gap between cards (design) */}
            <Grid container spacing={1.5}>
                {(isFetchingStats || (!statsData && !statsError)
                    ? [1, 2, 3, 4]
                    : filteredStats
                ).map((stat: any, index: any) => (
                    <Grid item key={index} xs={6} md={4} lg={3}>
                        <FunnelKpiCard
                            isFetching={isFetchingStats || (!statsData && !statsError)}
                            title={stat.title}
                            compare={stat.compare}
                            definition={stat?.definition}
                            invertedColor={stat?.invertedColor}
                            isPercent={stat.isPercent}
                            isCurrency={stat?.isCurrency}
                            total={stat.value}
                            hideInfinity={stat?.hideInfinity}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Graph */}
            <Paper
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#212b36" : "white",
                    boxShadow:
                        "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
                    height: "500px",
                    borderRadius: 2,
                    mt: 2,
                    mb: 0,
                }}
            >
                {isFetchingGraph || (!performanceGraphData && !performanceGraphError) ? (
                    <Skeleton
                        width="100%"
                        height="100%"
                        variant="rectangular"
                        animation="wave"
                        style={{ borderRadius: 10 }}
                    />
                ) : (
                    <Box p={3} height="100%">
                        <Typography variant="overline" color="textSecondary" sx={{ mb: 1 }}>
                            Impact of Primelis Signal
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 2, fontSize: 16 }}>
                            Ad Spend Saved on Brand Traffic (Before & After)
                        </Typography>
                        <Box sx={{ height: 370 }}>
                            <ChartPerformanceBusinessOverview
                                data={performanceGraphData?.performanceGraph || []}
                            />
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* DataGrid */}
            <Paper
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#212b36" : "white",
                    boxShadow:
                        "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
                    borderRadius: 2,
                    mt: 2,
                    mb: 1,
                }}
            >
                {isFetchingSplitRows ||
                    (!performanceSplitRows && !performanceSplitRowsError) ? (
                    <Skeleton
                        width="100%"
                        height={400}
                        variant="rectangular"
                        animation="wave"
                        style={{ borderRadius: 10 }}
                    />
                ) : (
                    <Box>
                        {performanceSplitRows?.performanceSplit?.some(
                            (item: any) => item.split
                        ) && (
                                <DataGridPerformanceTable
                                    data={performanceSplitRows.performanceSplit}
                                    split={splitOptions[value]}
                                    compare={filterStore?.compare}
                                    sinceCrossBrand={sinceActivationData?.first_entry_date}
                                    tabs={!disableTab ? tabsToShow : []}
                                    tabValue={value}
                                    onTabChange={handleChange}
                                />
                            )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
